import { TASK_STATUSES } from '../../types';
import type {
  ChannelFolderId,
  Folder,
  FolderId,
  HexColor,
  LucideIconName,
  MilestoneId,
  MockCache,
  Milestone,
  Project,
  ProjectId,
  ProjectKey,
  WorkspaceId,
} from '../../types';

import { maxSortKey, snapshot, sortKeyBetween } from '../internal';
import { logActivity } from '../journal';
import { bump, requireCache } from '../runtime';

interface Placed {
  id: string;
  sortKey: string;
}

const KEY_TRANSLITERATION: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm',
  н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sh',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};
const KEY_LETTERS = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'];
const KEY_SUFFIXES = [...KEY_LETTERS, ...KEY_LETTERS.flatMap((first) => KEY_LETTERS.map((second) => first + second))];
const FALLBACK_KEY = 'PRJ';
const KEY_BASE_LENGTH = 4;

export function sortKeyLast(siblings: readonly Placed[]): string {
  return sortKeyBetween(maxSortKey(siblings.map((sibling) => sibling.sortKey)), null);
}

export function sortKeyAfter(siblings: readonly Placed[], afterId: string | null): string {
  const ordered = [...siblings].sort((a, b) => (a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0));
  if (afterId === null) {
    return sortKeyBetween(null, ordered[0]?.sortKey ?? null);
  }
  const at = ordered.findIndex((sibling) => sibling.id === afterId);
  const anchor = ordered[at];
  if (anchor === undefined) {
    throw new Error('Соседний элемент не найден');
  }
  return sortKeyBetween(anchor.sortKey, ordered[at + 1]?.sortKey ?? null);
}

function requireName(raw: string, emptyMessage: string): string {
  const name = raw.trim();
  if (name.length === 0) {
    throw new Error(emptyMessage);
  }
  return name;
}

function requireFolder(cache: MockCache, id: FolderId, workspaceId: WorkspaceId): Folder {
  const folder = cache.folders.get(id);
  if (folder === undefined || folder.workspaceId !== workspaceId) {
    throw new Error('Папка не найдена');
  }
  return folder;
}

function foldersIn(cache: MockCache, workspaceId: WorkspaceId, parentId: FolderId | null, exceptId: FolderId | null) {
  return [...cache.folders.values()].filter(
    (folder) =>
      folder.workspaceId === workspaceId &&
      folder.parentId === parentId &&
      folder.archivedAt === null &&
      folder.id !== exceptId,
  );
}

function projectsIn(cache: MockCache, workspaceId: WorkspaceId, folderId: FolderId | null, exceptId: ProjectId | null) {
  return [...cache.projects.values()].filter(
    (project) =>
      project.workspaceId === workspaceId &&
      project.folderId === folderId &&
      project.archivedAt === null &&
      project.id !== exceptId,
  );
}

function milestonesOf(cache: MockCache, projectId: ProjectId, exceptId: MilestoneId | null) {
  return [...cache.milestones.values()].filter(
    (milestone) => milestone.projectId === projectId && milestone.archivedAt === null && milestone.id !== exceptId,
  );
}

function isSelfOrDescendant(cache: MockCache, candidateId: FolderId, folderId: FolderId): boolean {
  let current: FolderId | null = candidateId;
  while (current !== null) {
    if (current === folderId) return true;
    current = cache.folders.get(current)?.parentId ?? null;
  }
  return false;
}

function projectKeyFor(cache: MockCache, name: string): ProjectKey {
  let letters = '';
  for (const char of name.toLowerCase()) {
    letters += KEY_TRANSLITERATION[char] ?? char;
  }
  const onlyLetters = letters.replace(/[^a-z]/g, '').toUpperCase();
  const base = onlyLetters.length >= 2 ? onlyLetters.slice(0, KEY_BASE_LENGTH) : FALLBACK_KEY;
  const taken = new Set([...cache.projects.values()].map((project) => String(project.key)));
  if (!taken.has(base)) {
    return base as ProjectKey;
  }
  const stem = base.slice(0, KEY_BASE_LENGTH - 1);
  const free = KEY_SUFFIXES.map((suffix) => stem + suffix).find((key) => !taken.has(key));
  if (free === undefined) {
    throw new Error('Не удалось подобрать ключ проекта');
  }
  return free as ProjectKey;
}

function projectChangeKeys(id: ProjectId): string[] {
  return ['projects', 'workspace', 'tree', 'tasks', 'board', `board:${String(id)}`];
}

export function createFolder(args: { id: FolderId; parentId: FolderId | null; name: string }): Folder {
  const cache = requireCache();
  const name = requireName(args.name, 'Имя папки не может быть пустым');
  const workspaceId = cache.activeWorkspaceId;
  if (args.parentId !== null) {
    requireFolder(cache, args.parentId, workspaceId);
  }
  const now = cache.nowMs();
  const folder: Folder = {
    id: args.id,
    workspaceId,
    parentId: args.parentId,
    name,
    icon: 'Folder' as LucideIconName,
    color: 'var(--identity-1)' as HexColor,
    sortKey: sortKeyLast(foldersIn(cache, workspaceId, args.parentId, null)),
    archivedAt: null,
    createdAt: now,
    updatedAt: now,
  };
  cache.folders.set(folder.id, folder);
  logActivity(cache, {
    mutator: 'folder.create',
    entityTable: 'folder',
    entityId: String(folder.id),
    op: 'insert',
    before: null,
    after: snapshot(folder),
    taskIds: [],
  });
  bump('workspace', 'tree');
  return folder;
}

export function moveFolder(args: { id: FolderId; parentId: FolderId | null; afterId: FolderId | null }): void {
  const cache = requireCache();
  const folder = requireFolder(cache, args.id, cache.activeWorkspaceId);
  if (args.parentId !== null) {
    requireFolder(cache, args.parentId, folder.workspaceId);
    if (isSelfOrDescendant(cache, args.parentId, folder.id)) {
      throw new Error('Папку нельзя положить в её потомка');
    }
  }
  const before = { parentId: folder.parentId, sortKey: folder.sortKey };
  const sortKey = sortKeyAfter(foldersIn(cache, folder.workspaceId, args.parentId, folder.id), args.afterId);
  folder.parentId = args.parentId;
  folder.sortKey = sortKey;
  folder.updatedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'folder.move',
    entityTable: 'folder',
    entityId: String(folder.id),
    op: 'update',
    before,
    after: { parentId: folder.parentId, sortKey: folder.sortKey },
    taskIds: [],
  });
  bump('workspace', 'tree');
}

export function createProject(args: { id: ProjectId; folderId: FolderId | null; name: string }): Project {
  const cache = requireCache();
  const name = requireName(args.name, 'Имя проекта не может быть пустым');
  const workspaceId = cache.activeWorkspaceId;
  if (args.folderId !== null) {
    requireFolder(cache, args.folderId, workspaceId);
  }
  const now = cache.nowMs();
  const project: Project = {
    id: args.id,
    workspaceId,
    folderId: args.folderId,
    key: projectKeyFor(cache, name),
    name,
    icon: 'Kanban' as LucideIconName,
    color: `var(--identity-${(cache.projects.size % 8) + 1})` as HexColor,
    description: null,
    statusIds: [...TASK_STATUSES],
    typeIds: [...cache.taskTypes.values()]
      .filter((type) => type.workspaceId === workspaceId && type.archivedAt === null)
      .map((type) => type.id),
    memberIds: [cache.me],
    startOn: null,
    targetOn: null,
    nextTaskNumber: 1,
    sortKey: sortKeyLast(projectsIn(cache, workspaceId, args.folderId, null)),
    archivedAt: null,
    createdAt: now,
    updatedAt: now,
  };
  cache.projects.set(project.id, project);
  logActivity(cache, {
    mutator: 'project.create',
    entityTable: 'project',
    entityId: String(project.id),
    op: 'insert',
    before: null,
    after: snapshot(project),
    taskIds: [],
  });
  bump(...projectChangeKeys(project.id));
  return project;
}

export function moveProject(args: { id: ProjectId; folderId: FolderId | null; afterId: ProjectId | null }): void {
  const cache = requireCache();
  const project = cache.projects.get(args.id);
  if (project === undefined) {
    throw new Error('Проект не найден');
  }
  if (args.folderId !== null) {
    requireFolder(cache, args.folderId, project.workspaceId);
  }
  const before = { folderId: project.folderId, sortKey: project.sortKey };
  const sortKey = sortKeyAfter(projectsIn(cache, project.workspaceId, args.folderId, project.id), args.afterId);
  project.folderId = args.folderId;
  project.sortKey = sortKey;
  project.updatedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'project.move',
    entityTable: 'project',
    entityId: String(project.id),
    op: 'update',
    before,
    after: { folderId: project.folderId, sortKey: project.sortKey },
    taskIds: [],
  });
  bump(...projectChangeKeys(project.id));
}

export function archiveProject(args: { id: ProjectId }): void {
  const cache = requireCache();
  const project = cache.projects.get(args.id);
  if (project === undefined) {
    throw new Error('Проект не найден');
  }
  if (project.archivedAt !== null) return;
  const now = cache.nowMs();
  project.archivedAt = now;
  project.updatedAt = now;
  logActivity(cache, {
    mutator: 'project.archive',
    entityTable: 'project',
    entityId: String(project.id),
    op: 'update',
    before: { archivedAt: null },
    after: { archivedAt: now },
    taskIds: [],
  });
  bump(...projectChangeKeys(project.id));
}

export function createMilestone(args: { id: MilestoneId; projectId: ProjectId; name: string }): Milestone {
  const cache = requireCache();
  const name = requireName(args.name, 'Имя вехи не может быть пустым');
  const project = cache.projects.get(args.projectId);
  if (project === undefined) {
    throw new Error('Проект не найден');
  }
  const now = cache.nowMs();
  const milestone: Milestone = {
    id: args.id,
    workspaceId: project.workspaceId,
    projectId: project.id,
    name,
    icon: 'Diamond' as LucideIconName,
    description: '',
    color: project.color,
    dueOn: null,
    completedAt: null,
    sortKey: sortKeyLast(milestonesOf(cache, project.id, null)),
    archivedAt: null,
    createdAt: now,
    updatedAt: now,
  };
  cache.milestones.set(milestone.id, milestone);
  logActivity(cache, {
    mutator: 'milestone.create',
    entityTable: 'milestone',
    entityId: String(milestone.id),
    op: 'insert',
    before: null,
    after: snapshot(milestone),
    taskIds: [],
  });
  bump(`board:${String(project.id)}`, 'workspace', 'tree');
  return milestone;
}

export function moveMilestone(args: { id: MilestoneId; afterId: MilestoneId | null }): void {
  const cache = requireCache();
  const milestone = cache.milestones.get(args.id);
  if (milestone === undefined) {
    throw new Error('Веха не найдена');
  }
  const before = { sortKey: milestone.sortKey };
  milestone.sortKey = sortKeyAfter(milestonesOf(cache, milestone.projectId, milestone.id), args.afterId);
  milestone.updatedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'milestone.move',
    entityTable: 'milestone',
    entityId: String(milestone.id),
    op: 'update',
    before,
    after: { sortKey: milestone.sortKey },
    taskIds: [],
  });
  bump(`board:${String(milestone.projectId)}`, 'workspace', 'tree');
}

export function archiveMilestone(args: { id: MilestoneId }): void {
  const cache = requireCache();
  const milestone = cache.milestones.get(args.id);
  if (milestone === undefined) {
    throw new Error('Веха не найдена');
  }
  if (milestone.archivedAt !== null) return;
  const now = cache.nowMs();
  milestone.archivedAt = now;
  milestone.updatedAt = now;
  logActivity(cache, {
    mutator: 'milestone.archive',
    entityTable: 'milestone',
    entityId: String(milestone.id),
    op: 'update',
    before: { archivedAt: null },
    after: { archivedAt: now },
    taskIds: [],
  });
  bump(`board:${String(milestone.projectId)}`, 'workspace', 'tree');
}

export function moveChannelFolder(args: { id: ChannelFolderId; afterId: ChannelFolderId | null }): void {
  const cache = requireCache();
  const folder = cache.channelFolders.get(args.id);
  if (folder === undefined) {
    throw new Error('Папка не найдена');
  }
  const siblings = [...cache.channelFolders.values()].filter(
    (other) => other.workspaceId === folder.workspaceId && other.archivedAt === null && other.id !== folder.id,
  );
  folder.sortKey = sortKeyAfter(siblings, args.afterId);
  bump('channels', 'workspace');
}
